import type { ResolvedAddress } from "@vaulted/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { post } from "../api";
import { Field } from "./ui";

export interface AddressLookup {
  suggest: (q: string) => Promise<{ id: string; label: string }[]>;
  resolve: (id: string) => Promise<ResolvedAddress>;
}

/**
 * An address with suggestions as you type (staff screens). Picking one fills in the full address
 * on one line, or, with onResolved, hands back its parts (for forms with street, city, state, ZIP).
 */
export function AddressField({
  label = "Address",
  value,
  onChange,
  required,
  className,
  placeholder = "Start typing the street address",
  onResolved,
  lookup,
  disabled,
  labelClassName,
  inputClassName,
}: {
  label?: string;
  value: string;
  onChange: (address: string) => void;
  required?: boolean;
  className?: string;
  placeholder?: string;
  onResolved?: (address: ResolvedAddress) => void;
  /** Where to look addresses up when not signed in as staff (a forms link, the clinic iPad). */
  lookup?: AddressLookup;
  disabled?: boolean;
  labelClassName?: string;
  inputClassName?: string;
}) {
  const [typing, setTyping] = useState(false);
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value.trim()), 300);
    return () => clearTimeout(t);
  }, [value]);
  const suggestions = useQuery({
    queryKey: ["address-suggest", !!lookup, debounced],
    queryFn: () => (lookup ? lookup.suggest(debounced) : post<{ id: string; label: string }[]>("/api/addresses/suggest", { q: debounced })),
    enabled: typing && debounced.length >= 4,
    staleTime: 60_000,
  });
  const pick = async (id: string, label: string) => {
    setTyping(false);
    if (!onResolved) onChange(label);
    const found = await (lookup ? lookup.resolve(id) : post<ResolvedAddress>("/api/addresses/resolve", { id })).catch(() => null);
    if (!found) return;
    if (onResolved) onResolved(found);
    else onChange(found.label || `${found.line1}, ${found.city}, ${found.state} ${found.postalCode}`);
  };
  const list = typing ? (suggestions.data ?? []) : [];
  return (
    <div className={`relative ${className ?? ""}`}>
      <Field
        label={label}
        labelClassName={labelClassName}
        inputClassName={inputClassName}
        required={required}
        disabled={disabled}
        autoComplete="off"
        maxLength={onResolved ? 120 : 200}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          setTyping(true);
          onChange(e.target.value);
        }}
        onBlur={() => setTimeout(() => setTyping(false), 200)}
      />
      {list.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-[var(--radius-control)] border border-line bg-white shadow-lg">
          {list.map((s) => (
            <li key={s.id}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => void pick(s.id, s.label)} className="w-full border-b border-mist px-3 py-2 text-left text-sm last:border-0 hover:bg-mist">
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
